import React, { useState } from "react";
import { Box, Button } from "@material-ui/core";
import { HFlex, ProgressButton, Text, DialogSuccessNotice, useErrorContext } from "shared";
import { FormProvider, useForm } from "react-hook-form";
import { useCreateHcpService, useCreateHcpServiceCategory } from "api/hooks/useHcpServices";
import ServiceFormFields, { ServiceFormData, formValuesToPayload, serviceFormDefaults } from "./ServiceFormFields";

interface Props {
  hcpId: number;
  onSuccess: () => void;
  onCancel: () => void;
}

const CreateServiceForm: React.VFC<Props> = ({ hcpId, onSuccess, onCancel }) => {
  const setError = useErrorContext();
  const [successData, setSuccessData] = useState<{ heading: string; body: string } | null>(null);

  const form = useForm<ServiceFormData>({
    defaultValues: {
      name: "",
      description: "",
      duration: "30",
      mode: "digital",
      purpose: "general",
      payment_methods: ["free"],
      ...serviceFormDefaults,
      capacity: "1",
      health_questionnaire_type: "none",
      bookable_by_patients: false,
      health_care_professional_ids: [],
    },
  });

  const createServiceMutation = useCreateHcpService();
  const createCategoryMutation = useCreateHcpServiceCategory();
  const isLoading = createServiceMutation.isLoading || createCategoryMutation.isLoading;

  const submit = form.handleSubmit(async fields => {
    try {
      const newCategory = await createCategoryMutation.mutateAsync({
        hcpId,
        name: fields.name,
        description: fields.name + " description",
      });

      const created = await createServiceMutation.mutateAsync({
        hcpId,
        ...formValuesToPayload(fields),
        health_care_provider_service_category_id: newCategory.id,
      });

      setSuccessData({
        heading: "Service created successfully",
        body: `${created.name} (ID: ${created.id})`,
      });
    } catch (err) {
      setError("Could not create service", err);
    }
  });

  if (successData)
    return <DialogSuccessNotice heading={successData.heading} body={successData.body} onClose={() => onSuccess()} />;

  return (
    <Box mx={3}>
      <Text heading bold mb={3}>
        Create Service
      </Text>
      <FormProvider {...form}>
        <form onSubmit={submit}>
          <ServiceFormFields hcpId={hcpId} paymentMethodsDefault={["free"]} />

          <HFlex justifyContent="flex-end" gap={2} my={4}>
            <Button disabled={isLoading} onClick={onCancel}>
              Cancel
            </Button>
            <ProgressButton loading={isLoading}>Create</ProgressButton>
          </HFlex>
        </form>
      </FormProvider>
    </Box>
  );
};

export default CreateServiceForm;
